import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { getRestauranteActual } from "../api/public";
import { getImageUrl } from "../utils/imageUrl";
import { Eye, EyeOff } from "lucide-react";
import fondo from "../assets/fondo-restaurante.jpg";

export default function LoginPage() {
  const { iniciarSesion, isAuthenticated, usuario, cerrarSesion } = useAuth();
  const navigate = useNavigate();
  const [correo, setCorreo] = useState("");
  const [contrasenia, setContrasenia] = useState("");
  const [mostrarContrasenia, setMostrarContrasenia] = useState(false);
  const [error, setError] = useState("");
  const [cargando, setCargando] = useState(false);
  const [logo, setLogo] = useState(null);

  useEffect(() => {
    if (isAuthenticated) {
      const tieneAcceso =
        usuario?.isSuperAdmin ||
        usuario?.id_superadmin ||
        usuario?.rol === "superAdmin" ||
        ["admin", "mesero"].includes(usuario?.rol);

      if (tieneAcceso) {
        navigate("/dashboard", { replace: true });
      } else if (usuario) {
        // Si la sesión almacenada no tiene rol administrativo, cerramos para mostrar el formulario
        cerrarSesion();
      }
    }
  }, [isAuthenticated, usuario, navigate, cerrarSesion]);

  useEffect(() => {
    const cargarLogo = async () => {
      try {
        const restaurante = await getRestauranteActual();
        if (restaurante?.logo) {
          const logoUrl = `${getImageUrl(restaurante.logo)}?t=${Date.now()}`;
          setLogo(logoUrl);
        }
      } catch (err) {
        console.error('Error cargando logo en LoginPage:', err);
      }
    };

    cargarLogo();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setCargando(true);
    try {
      await iniciarSesion({ correo, contrasenia });
      navigate("/dashboard");
    } catch (err) {
      setError(typeof err === "string" ? err : err.message || "Credenciales inválidas");
    } finally {
      setCargando(false);
    }
  };

  return (
    <div
      className="flex items-center justify-center min-h-screen bg-cover bg-center"
      style={{ backgroundImage: `url(${fondo})` }}
    >
      <form
        onSubmit={handleSubmit}
        className="bg-amber-950/60 p-8 rounded-3xl shadow-2xl w-96 max-w-full backdrop-blur-md border border-amber-900/30"
      >
        {/* Logo del restaurante */}
        <div className="flex justify-center mb-6">
          {logo && <img src={logo} alt="Logo Restaurante" className="h-36 w-auto object-contain drop-shadow-md" />}
        </div>

        <h2 className="text-2xl font-bold mb-6 text-center text-white drop-shadow-md">
          Iniciar Sesión
        </h2>

        {/* Campo correo */}
        <div className="mb-4">
          <input
            type="email"
            placeholder="Correo electrónico"
            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition bg-white/95 text-gray-900"
            value={correo}
            onChange={(e) => setCorreo(e.target.value)}
            required
          />
        </div>

        {/* Campo contraseña con toggle de visibilidad */}
        <div className="relative mb-4">
          <input
            type={mostrarContrasenia ? "text" : "password"}
            placeholder="Contraseña"
            className="w-full p-3 pr-11 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition bg-white/95 text-gray-900"
            value={contrasenia}
            onChange={(e) => setContrasenia(e.target.value)}
            required
          />
          <button
            type="button"
            onClick={() => setMostrarContrasenia(!mostrarContrasenia)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-amber-800 focus:outline-none p-1 transition"
            title={mostrarContrasenia ? "Ocultar contraseña" : "Ver contraseña"}
            tabIndex={-1}
          >
            {mostrarContrasenia ? <EyeOff size={20} /> : <Eye size={20} />}
          </button>
        </div>

        {error && (
          <p className="text-red-400 font-medium text-sm mb-4 text-center bg-red-950/40 p-2.5 rounded-lg border border-red-500/30">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={cargando}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg transition font-semibold shadow-lg hover:shadow-xl disabled:opacity-60 flex items-center justify-center gap-2"
        >
          {cargando ? (
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
          ) : (
            "Entrar"
          )}
        </button>

        <p className="text-sm text-center text-amber-100 font-medium mt-5 drop-shadow">
          ¿Olvidaste tu contraseña?{" "}
          <Link
            to="/recuperar-contraseña"
            className="text-amber-300 hover:text-amber-200 font-bold underline underline-offset-4 transition ml-1"
          >
            Recuperar
          </Link>
        </p>
      </form>
    </div>
  );
}
